import { Button, FormControl, Grid, Typography } from '@mui/material';
import MultipleCheckBoxes from '../UI/MultipleCheckBoxes';
import MultiSelectInput from '../UI/MultiSelectInput';
import GenericInput from '../UI/GenericInput';
import DepartmentServeInfoDay from './DepartmentServeInfoDay';
import { title } from '../../../../assets/styles/appStyles';
import Panel from '../../../../components/UI/Panel';

export default function DepartmentServeInfo({
  form,
  serveMethods,
  timeZones,
  days,
}: Readonly<{
  form: any;
  timeZones: any[];
  serveMethods: any[];
  days: any[];
}>) {
  const onChangeServeMethods = (id: string) => {
    const newItems = [
      ...(form?.values?.custodianDetails?.serveMethods ?? []),
    ];
    const itemIndex = newItems.findIndex((i: any) => i.serveMethodId === id);
    if (itemIndex > -1) {
      newItems.splice(itemIndex, 1);
    } else {
      newItems.push({ serveMethodId: id });
    }
    form.setFieldValue(
      `custodianDetails.serveMethods`,
      newItems,
    );
  };
  const onChangeRoiMethods = (id: string) => {
    const newItems = [
      ...(form?.values?.custodianDetails?.roiMethods ?? []),
    ];
    const itemIndex = newItems.findIndex((i: any) => i.serveMethodId === id);
    if (itemIndex > -1) {
      newItems.splice(itemIndex, 1);
    } else {
      newItems.push({ serveMethodId: id });
    }
    form.setFieldValue(
      `custodianDetails.roiMethods`,
      newItems,
    );
  };

  const onChangeDays = (d: string) => {
    let daysAvailable = [
      ...(form?.values?.custodianDetails?.daysAvailable ?? []),
    ];
    const recordIndex = daysAvailable.findIndex(
      (record) => record.dayOfWeek === d,
    );
    if (recordIndex > -1) {
      daysAvailable = 
        daysAvailable.filter((record) => record.dayOfWeek !== d)
      ;
    } else
      daysAvailable = [
        ...daysAvailable,
        {
          dayOfWeek: d,
          timeZoneId: null,
          serveHours: { from: '', to: '' },
          pickupAndCopyHours: { from: '', to: '' },
          bestTimeToCall: { from: '', to: '' },
        },
      ];
    form.setFieldValue(
      `custodianDetails.daysAvailable`,
      daysAvailable,
    );
  };

  const onSelectAllDays = () => {
    const datesSelectedBefore =
      form?.values?.custodianDetails?.daysAvailable;
    form.setFieldValue(`custodianDetails.daysAvailable`, 
      days.map((d) => {
        const daySelectedBefore = datesSelectedBefore.find(
          (b: any) => b.dayOfWeek === d.id,
        );
        return {
          dayOfWeek: d.id,
          timeZoneId: daySelectedBefore?.timeZoneId,
          serveHours: daySelectedBefore?.serveHours || { from: '', to: '' },
          bestTimeToCall: daySelectedBefore?.serveHours || {
            from: '',
            to: '',
          },
          pickupAndCopyHours: daySelectedBefore?.pickupAndCopyHours || {
            from: '',
            to: '',
          },
        };
      })
    );
  };

  const enableServeNotes = form?.values?.custodianDetails?.serveMethods?.length > 0;
  const enableRoiNotes = form?.values?.custodianDetails?.roiMethods?.length > 0;

  const serveMethodsOption = serveMethods.filter(m => m.name !== "Pick up");
  const roiMethodsOption = serveMethods.filter(m => m.name !== "Hand serve");

  return (
    <Panel headerText="Serve Information">
      <Grid container spacing={2} pt={2}>
        <Grid xs={6} item>
          <MultipleCheckBoxes
            options={serveMethodsOption}
            keyName={`${form}.values.serveMethods`}
            label="Serve Method"
            selectedValues={form?.values?.custodianDetails?.serveMethods?.map(
              (o: any) => o.serveMethodId,
            )}
            toggleChange={onChangeServeMethods}
          />
        </Grid>
        <Grid item xs={4} px={2} py={2}>
          <FormControl fullWidth>
            <GenericInput
              name={`custodianDetails.serveMethodNotes`}
              label="Notes"
              maxLength={150}
              placeholder="Notes"
              disabled={!enableServeNotes}
              value={form?.values?.custodianDetails?.serveMethodNotes}
              onChange={(e: any) => {
                form.setFieldValue(`custodianDetails.serveMethodNotes`, e.target.value);
              }}
              errorMsg={
                form.touched.custodianDetails?.serveMethodNotes &&
                  form.errors.custodianDetails?.serveMethodNotes
                  ? form.errors.custodianDetails.serveMethodNotes
                  : ''
              }
              hasError={
                !!(form.touched.custodianDetails?.serveMethodNotes &&
                  form.errors.custodianDetails?.serveMethodNotes)
              }
              onBlur={() => {
                form.setFieldTouched(
                  `custodianDetails.serveMethodNotes`,
                  true,
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid xs={6} item>
          <MultipleCheckBoxes
            options={roiMethodsOption}
            keyName={`${form}.values.roiMethods`}
            label="ROI"
            selectedValues={form?.values?.custodianDetails?.roiMethods?.map(
              (o: any) => o.serveMethodId,
            )}
            toggleChange={onChangeRoiMethods}
          />
        </Grid>
        <Grid item xs={4} px={2} py={2}>
          <FormControl fullWidth>
            <GenericInput
              name={`custodianDetails.roiMethodNotes`}
              label="Notes"
              maxLength={150}
              placeholder="Notes"
              disabled={!enableRoiNotes}
              value={form?.values?.custodianDetails?.roiMethodNotes}
              onChange={(e: any) => {
                form.setFieldValue(`custodianDetails.roiMethodNotes`, e.target.value);
              }}
              errorMsg={
                form.touched.custodianDetails?.roiMethodNotes &&
                  form.errors.custodianDetails?.roiMethodNotes
                  ? form.errors.custodianDetails.roiMethodNotes
                  : ''
              }
              hasError={
                !!(form.touched.custodianDetails?.roiMethodNotes &&
                  form.errors.custodianDetails?.roiMethodNotes)
              }
              onBlur={() => {
                form.setFieldTouched(
                  `custodianDetails.roiMethodNotes`,
                  true,
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid xs={12} item>
          <Typography sx={title}>Select days available for pickup</Typography>
          <Grid container alignItems={'center'}>
            <Grid item>
              <MultiSelectInput
                errorMsg=""
                options={days}
                togglePress={onChangeDays}
                values={form?.values?.custodianDetails?.daysAvailable?.map((o: { dayOfWeek: any; }) => o.dayOfWeek)}
              />
            </Grid>
            {form?.values?.custodianDetails?.daysAvailable?.length !== days.length && (
              <Grid item pr={1} pt={1}>
                <Button
                  name="select-all"
                  variant="outlined"
                  onClick={onSelectAllDays}
                >
                  Select All
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid xs={12} item>
          {(form?.values?.custodianDetails?.daysAvailable ?? [])
            .sort((d1: { dayOfWeek: string | number; }, d2: { dayOfWeek: string | number; }) => +d1.dayOfWeek - +d2.dayOfWeek)
            .map((d: any, index: number) => (
              <DepartmentServeInfoDay
                key={d.dayOfWeek}
                day={d}
                dayIndex={index}
                days={days}
                form={form}
                handServeId={
                  serveMethods.find(
                    (s) => s.name?.toLowerCase() === 'hand serve',
                  )?.id
                }
                timeZones={timeZones}
              />
            ))}
        </Grid>
      </Grid>
    </Panel>
  );
}
